import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { MailX, PhoneOff, Trash2, Plus, ShieldCheck, ShieldAlert, ShieldX, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const ListManager = ({ title, desc, icon: Icon, items, idKey, placeholder, onAdd, onRemove, testidPrefix }) => {
  const [val, setVal] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!val.trim()) return;
    setBusy(true);
    try { await onAdd(val.trim()); setVal(''); } finally { setBusy(false); }
  };
  return (
    <Card data-testid={`${testidPrefix}-card`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base"><Icon className="h-4 w-4" />{title}</CardTitle>
        <CardDescription>{desc}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder={placeholder}
            onKeyDown={(e) => e.key === 'Enter' && submit()} data-testid={`${testidPrefix}-input`} />
          <Button onClick={submit} disabled={busy} data-testid={`${testidPrefix}-add`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          </Button>
        </div>
        <div className="space-y-1.5 max-h-64 overflow-y-auto">
          {items.length === 0 && <p className="text-xs text-muted-foreground py-2">No entries yet.</p>}
          {items.map((it) => (
            <div key={it[idKey]} className="flex items-center justify-between gap-2 rounded-md border border-border/40 bg-card/40 px-3 py-2" data-testid={`${testidPrefix}-row`}>
              <div className="min-w-0">
                <p className="text-sm text-foreground truncate">{it[idKey]}</p>
                {it.reason && <Badge variant="secondary" className="mt-0.5 text-[10px]">{it.reason}</Badge>}
              </div>
              <button onClick={() => onRemove(it[idKey])} className="text-muted-foreground hover:text-red-400 transition-colors" data-testid={`${testidPrefix}-remove`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export const DeliverabilityManager = () => {
  const [supp, setSupp] = useState([]);
  const [dnc, setDnc] = useState([]);
  const [auth, setAuth] = useState(null);
  const [wh, setWh] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, d, a] = await Promise.all([
        api.get('/api/deliverability/suppressions'),
        api.get('/api/deliverability/dnc'),
        api.get('/api/deliverability/domain-auth').catch(() => ({ data: null })),
      ]);
      setSupp(s.data?.suppressions || []);
      setDnc(d.data?.dnc || []);
      setAuth(a.data);
    } catch (e) { /* non-blocking */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addSupp = async (email) => {
    try { await api.post('/api/deliverability/suppressions', { email, reason: 'manual' }); toast.success('Suppressed'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const rmSupp = async (email) => { await api.delete(`/api/deliverability/suppressions/${encodeURIComponent(email)}`); toast.success('Removed'); load(); };
  const addDnc = async (phone) => {
    try { await api.post('/api/deliverability/dnc', { phone, reason: 'manual' }); toast.success('Added to DNC'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const rmDnc = async (phone) => { await api.delete(`/api/deliverability/dnc/${encodeURIComponent(phone)}`); toast.success('Removed'); load(); };

  const AuthIcon = auth?.status === 'pass' ? ShieldCheck : auth?.status === 'partial' ? ShieldAlert : ShieldX;
  const tone = auth?.status === 'pass' ? 'text-emerald-400' : auth?.status === 'partial' ? 'text-amber-400' : 'text-red-400';

  return (
    <div className="space-y-6" data-testid="deliverability-manager">
      {/* Bounce/complaint webhook connection */}
      <Card data-testid="webhook-config-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><MailX className="h-4 w-4" />Bounce &amp; complaint webhook</CardTitle>
          <CardDescription>Paste this URL into your email provider (SendGrid → Event Webhook) so bounces, spam complaints and unsubscribes auto-suppress.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {!wh ? (
            <Button variant="outline" onClick={async () => {
              try { const r = await api.get('/api/deliverability/webhook-config'); setWh(r.data); }
              catch (e) { toast.error('Could not load webhook config'); }
            }} data-testid="webhook-reveal-btn">Reveal webhook URL</Button>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <Input readOnly value={wh.webhook_url || '(set BACKEND_PUBLIC_URL)'} className="font-mono text-xs" data-testid="webhook-url" />
                <Button variant="outline" onClick={() => { navigator.clipboard.writeText(wh.webhook_url || ''); toast.success('Copied'); }} data-testid="webhook-copy">Copy</Button>
              </div>
              <p className="text-xs text-muted-foreground">Enable events: {(wh.events_to_enable || []).join(', ')}. {wh.provider_hint}</p>
            </>
          )}
        </CardContent>
      </Card>

      {auth && !auth.error && (
        <Card data-testid="domain-auth-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><AuthIcon className={`h-4 w-4 ${tone}`} />Sending domain authentication · {auth.domain}</CardTitle>
            <CardDescription>SPF, DKIM and DMARC protect inbox placement and sender reputation.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              {['spf', 'dkim', 'dmarc'].map((k) => (
                <div key={k} className={`flex-1 rounded-lg border px-3 py-2 text-center ${auth[k]?.present ? 'border-emerald-500/30 bg-emerald-500/[0.06]' : 'border-red-500/25 bg-red-500/[0.04]'}`} data-testid={`domain-auth-${k}`}>
                  <p className="text-xs font-medium uppercase text-foreground">{k}</p>
                  <p className={`text-[11px] mt-0.5 ${auth[k]?.present ? 'text-emerald-400' : 'text-red-400'}`}>{auth[k]?.present ? 'OK' : 'Missing'}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      <div className="grid gap-6 md:grid-cols-2">
        <ListManager title="Email suppression list" desc="These addresses are never emailed (bounces, complaints, unsubscribes)."
          icon={MailX} items={supp} idKey="email" placeholder="email@example.com" onAdd={addSupp} onRemove={rmSupp} testidPrefix="suppression" />
        <ListManager title="Do-Not-Call (DNC) list" desc="These numbers are never called by the autonomous voice engine."
          icon={PhoneOff} items={dnc} idKey="phone" placeholder="+1 415 555 0142" onAdd={addDnc} onRemove={rmDnc} testidPrefix="dnc" />
      </div>
    </div>
  );
};

export default DeliverabilityManager;
